import React from 'react'
import { graphql } from 'gatsby'
import { BREAKPOINT, COLOR } from '@farewill/ui/tokens'
import { Divider, Wrapper } from '@farewill/ui'

import PATHS from 'paths'
import { TRUSTPILOT_RATING_OUT_OF_5, TRUSTPILOT_REVIEWS_TOTAL } from 'config'
import { PRODUCTS } from 'lib/products/constants'
import { TRACKING_TYPES } from 'lib/tracking/constants'
import { roundDownToNearestHundred } from 'lib/formatting/numbers'
import DefaultLayout from 'layouts/DefaultLayout'
import BreakpointSwitch from 'components/BreakpointSwitch'
import CoBrandedHeader from 'components/Header/Custom/CoBranded'
import GetStarted from 'views/wills/components/Overview/GetStarted'

import Hero from '../slices/Hero'
import Trustpilot from '../slices/Trustpilot'
import Pledges from '../slices/Pledges'
import FAQ from '../slices/FAQ'
import WillOptions from '../slices/WillOptions'
import GiftStories from '../slices/GiftStories'
import GiftSlider from '../slices/GiftSlider'

import type { GreyLabelLandingPageProps } from '../types'

const GreyLabelLandingPage = ({
  data: {
    page: {
      utmCampaign,
      utmMedium,
      partner,
      heroTitle,
      heroSubtitle,
      heroCtaText,
    },
    faqSection,
  },
}: GreyLabelLandingPageProps): React.ReactElement => (
  <DefaultLayout
    branding={partner.utmSource}
    title="Online Will Writing Service | Legal Will In 15 Minutes"
    description={`Our solicitor-approved guide helps you write a will online from the comfort of your own home. Rated ${TRUSTPILOT_RATING_OUT_OF_5} out of 5 by over ${roundDownToNearestHundred(
      TRUSTPILOT_REVIEWS_TOTAL
    )} customers on Trustpilot. National Will Writing Firm of the Year.`}
    meta={[
      {
        name: 'robots',
        content: 'noindex',
      },
    ]}
    customHeader={
      <CoBrandedHeader
        altText="British Heart Foundation"
        assetDirectory="bhf"
        partnerLogoUrl="https://www.bhf.org.uk/"
      />
    }
    product={PRODUCTS.WILLS}
    canonicalPath={PATHS.WILLS.OVERVIEW}
    headerColor={COLOR.WHITE}
    footerType="compact"
    utmParameters={{
      partnerId: partner?.partnerId || TRACKING_TYPES.NOT_SET,
      utmCampaign: utmCampaign || TRACKING_TYPES.NOT_SET,
      utmMedium: utmMedium || TRACKING_TYPES.NOT_SET,
      utmSource: partner?.utmSource || TRACKING_TYPES.NOT_SET,
      utmChannel: partner?.utmChannel || TRACKING_TYPES.NOT_SET,
    }}
  >
    <Wrapper background={COLOR.WHITE}>
      <Hero
        assetDirectory="bhf"
        title={heroTitle || 'Write your Will for free today'}
        subtitle={heroSubtitle?.heroSubtitle}
        ctaText={heroCtaText || 'Let’s get started'}
        minHeight={650}
        imageTransformationParams={{
          small:
            'c_crop,x_1052,y_1475,w_3400,h_1889/ar_1.8,c_fill,f_auto,q_auto,w_600',
          medium:
            'c_crop,x_1012,y_1600,w_3479,h_1560/ar_2.23,c_fill,f_auto,q_auto,w_964',
          large:
            'c_crop,x_1061,y_162,w_3412,h_3250/ar_1.05,c_fill,f_auto,q_auto,h_650',
          extraLarge:
            'c_crop,x_1010,y_978,w_3408,h_2434/ar_1.4,c_fill,f_auto,q_auto,h_650',
        }}
        telephoneWillsEnabled={false}
      />

      <WillOptions
        partnerName="British Heart Foundation"
        telephoneWillsEnabled={false}
      />

      <Wrapper padding={['XL', 0, 'XXL']} paddingFromL={[96, 0, 'XXL']}>
        <GiftStories
          stories={[
            {
              title: 'Esther survived a heart attack, but many don’t.',
              description:
                'BHF saved my life. They can help save more. Their innovation, especially in digital and AI-led research, is transforming heart care for the future. A gift in your will can make that future possible.',
              source: 'Esther was 45 when she had a heart attack.',
              image:
                'com-assets/greylabel-landing-page-assets/bhf/case-study-1',
            },
            {
              title: 'Salman survived a heart attack, but many don’t.',
              description:
                'As a doctor and someone who has had a heart attack themselves, I know just how important it is that BHF continue to fund research into heart disease, improving outcomes for people. If it wasn’t for previous research, I don’t think I would be alive today.',
              source: 'Salman was 34 when he had a heart attack.',
              image:
                'com-assets/greylabel-landing-page-assets/bhf/case-study-2',
            },
          ]}
          title="Gifts in Wills support over 50% of research funded by British Heart Foundation"
        />
      </Wrapper>

      <GiftSlider
        caption="This calculation uses an average estate value of £505,000"
        partner="British Heart Foundation"
        intro="Move the slider to see the impact you could have by leaving part of your estate in your Will. You’re not obligated to leave a gift in your Will when using this service, but British Heart Foundation sincerely thanks you if you do."
        config={[
          {
            value: 0,
            label: '5%',
            text: '5% left to British Heart Foundation could buy a fluorescence microscope that allows researchers to observe what is going on inside heart or blood vessel cells.',
            image:
              'ar_1,c_fill,f_auto,q_auto/com-assets/greylabel-landing-page-assets/bhf/gift-5',
          },
          {
            value: 25,
            label: '10%',
            text: '10% left to British Heart Foundation could support a basic science PhD student for 2 years.',
            image:
              'ar_1,c_fill,f_auto,q_auto,w_300/com-assets/greylabel-landing-page-assets/bhf/gift-10',
          },
          {
            value: 50,
            label: '25%',
            text: '25% left to British Heart Foundation could fund the salary of a basic science early career research fellow for 3 years.',
            image:
              'ar_1,c_fill,f_auto,q_auto,w_300/com-assets/greylabel-landing-page-assets/bhf/gift-25',
          },
          {
            value: 75,
            label: '50%',
            text: '50% left to British Heart Foundation could fund a cardiovascular research project for 3 years, including research staff and funds for the equipment and laboratory materials needed for the project.',
            image:
              'ar_1,c_fill,f_auto,q_auto,w_300/com-assets/greylabel-landing-page-assets/bhf/gift-50',
          },
          {
            value: 100,
            label: '100%',
            text: '100% left to British Heart Foundation could fund a yearly cohort of 3 PhD students in the British Heart Foundation 4-year PhD programme, receiving outstanding training opportunities to become future leaders in cardiovascular research.',
            image:
              'ar_1,c_fill,f_auto,q_auto,w_300/com-assets/greylabel-landing-page-assets/bhf/gift-100',
          },
        ]}
      />

      <Wrapper container noTrim>
        <Trustpilot
          tag="BHF"
          title="Farewill is trusted by British Heart Foundation supporters"
        />
        <BreakpointSwitch breakAt={BREAKPOINT.M} aboveComponent={<Divider />} />
        <FAQ
          questions={faqSection.questions}
          imagePath="com-assets/greylabel-landing-page-assets/bhf/faq"
        />
        <BreakpointSwitch breakAt={BREAKPOINT.M} aboveComponent={<Divider />} />
        <Pledges
          title="Leave a gift in your Will and keep us beating"
          description="British Heart Foundation is funding research using AI that could help predict heart attacks up to 10 years in advance."
          imagePath="fl_sanitize/cobranding-partner-logos/bhf/logo"
        />
      </Wrapper>

      <GetStarted
        partnerCampaignReferral
        telephoneWillsEnabled={false}
        title="Ready to write your will?"
      />
    </Wrapper>
  </DefaultLayout>
)

export const query = graphql`
  query BHFGreyLabelPartnerLandingPage($slug: String!) {
    page: contentfulPartnerCampaignPage(slug: { eq: $slug }) {
      utmCampaign
      utmMedium
      heroTitle
      heroSubtitle {
        heroSubtitle
      }
      heroCtaText
      partner {
        partnerId
        telephoneWillsEnabled
        utmSource
        utmChannel
      }
      telephoneWillsEnabled
    }
    faqSection: contentfulFaqSection(
      contentful_id: { eq: "69ZxesKJkC99ZO82eqKIHC" }
    ) {
      ...FaqSectionFields
    }
  }
`

export default GreyLabelLandingPage
